import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { dePatientByIdQuery$data } from "@/modules/patients/services/de/__generated__/dePatientByIdQuery.graphql"

export type PatientData = dePatientByIdQuery$data["patientById"] | chPatientByIdQuery$data["patientById"]

interface PatientFileStore {
  patientFileInfo: PatientFragment$data | null
  patientData: PatientData | null
  setPatientFileInfo: (data: PatientFragment$data) => void
  setPatientData: (data: PatientData) => void
}

type PatientFileStoreType = ReturnType<typeof createPatientFileStore>

const createPatientFileStore = () => {
  return create<PatientFileStore>((set) => ({
    patientFileInfo: null,
    patientData: null,
    setPatientFileInfo: (data) => {
      set({ patientFileInfo: data })
    },
    setPatientData: (data) => {
      set({ patientData: data })
    },
  }))
}

export const PatientFileContext = createContext<PatientFileStoreType | null>(null)

type PatientFileProviderProps = React.PropsWithChildren

export const PatientFileProvider = ({ children }: PatientFileProviderProps) => {
  const storeRef = useRef<PatientFileStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createPatientFileStore()
  }
  return <PatientFileContext.Provider value={storeRef.current}>{children}</PatientFileContext.Provider>
}

export function usePatientFileContext<T>(selector: (state: PatientFileStore) => T): T {
  const store = useContext(PatientFileContext)
  if (!store) throw new Error("Missing PatientFileProvider in the tree")
  return useStore(store, selector)
}
